import React, { FC } from 'react';
import { CommentsList } from 'app/modules/posts/modules/post/components/postCommunication/CommentsList';
import { useCommentsThreadQuery } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useCommentsThreadQuery';
import { usePostCommunicationContext } from '../PostCommunicationProvider';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { Alert, Button, mergeTailwindClasses } from '@kontentino/ui';
import CommentSkeleton from './commentsList/CommentSkeleton';
import { useMergeRefs } from 'app/hooks/useMergeRefs';
import GoToNewestCommentButton from './GoToNewestCommentButton';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import CommentsClientAssignedUsers from 'app/modules/posts/modules/post/components/postCommunication/comments/CommentsClientAssignedUsers';
import CommentsClientActiveApprovalRequest from 'app/modules/posts/modules/post/components/postCommunication/comments/commentsThreads/CommentsClientActiveApprovalRequest';
import clsx from 'clsx';
import { useStickyApprovalRequest } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useStickyApprovalRequest';
import { animated } from '@react-spring/web';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';

type Props = {
  className?: string;
};

type ShouldShowCommentsEmptyStateParams = {
  count: number;
  isLoading: boolean;
  isFetching: boolean;
};

const shouldShowCommentsEmptyState = ({
  count,
  isLoading,
  isFetching,
}: ShouldShowCommentsEmptyStateParams): boolean =>
  count === 0 && !isLoading && !isFetching;

const CommentsThreads: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const {
    postId,
    activeContent,
    enableRespondToApprovalRequest,
    commentsListRef,
    user: currentUser,
    commentAction,
    scrollToActiveRequestComment,
    emptyState,
  } = usePostCommunicationContext();
  const comments = useCommentsThreadQuery({
    postId,
    thread: activeContent,
  });
  const ref = useMergeRefs(commentsListRef, comments.rootRef);
  const {
    onCommentWorkflowsIsVisibleChange,
    clientTransition,
    agencyTransition,
  } = useStickyApprovalRequest({
    commentsLength: comments.data.length,
  });

  const hasComments = comments.data.length > 0;
  const showEmptyState = shouldShowCommentsEmptyState({
    count: comments.data.length,
    isLoading: comments.isLoading,
    isFetching: comments.isFetching,
  });
  const isLoadingEmptyThread = !hasComments && !showEmptyState;

  const isUserClient = currentUser.role === 'client';
  const isPinnedContent = activeContent === PostCommunicationContent.PINNED;
  const isReplying = commentAction === 'reply';
  const showClientAssignedUsers =
    !isReplying &&
    PostCommunicationContent.CLIENT === activeContent &&
    currentUser.role !== 'client';

  return (
    <div
      className={mergeTailwindClasses(
        clsx(
          'tw-relative tw-flex tw-max-h-[25rem] tw-min-h-[15rem] tw-flex-col tw-gap-2 tw-overflow-hidden tw-py-2',

          {
            'tw-max-h-[calc(80vh-8rem)]': isUserClient,
          },
          className,
        ),
      )}
    >
      {clientTransition(
        (style, item) =>
          item && (
            <UserPermissionGate scopes={UserPermission.POST_CLIENT_APPROVE}>
              <animated.div
                style={style}
                className="tw-relative tw-z-10 tw-border-b tw-border-grayscale-20 tw-bg-white tw-pb-2"
                data-name="comments_client-floating-active-approval-request"
              >
                <CommentsClientActiveApprovalRequest />
              </animated.div>
            </UserPermissionGate>
          ),
      )}
      <div
        className={clsx('tw-flex tw-flex-1 tw-gap-1 tw-overflow-y-auto', {
          'tw-flex-col-reverse': !isPinnedContent,
          'tw-flex-col': isPinnedContent,
        })}
        ref={ref}
        data-name="comments_list"
      >
        <>
          {isLoadingEmptyThread && <Skeleton className="tw-h-full" />}
          {comments.error && <Alert> {t('somethingWentWrongTryAgain')}</Alert>}
          {!isLoadingEmptyThread && !comments.error && (
            <CommentsList
              emptyState={emptyState}
              currentUser={currentUser}
              comments={comments.data}
              enableRespondToApprovalRequest={enableRespondToApprovalRequest}
              onCommentWorkflowsIsVisibleChange={
                onCommentWorkflowsIsVisibleChange
              }
            />
          )}
        </>
        {comments.hasNextPage && (
          <div ref={comments.ref}>
            <CommentSkeleton />
          </div>
        )}
      </div>
      {agencyTransition(
        (style, item) =>
          item && (
            <UserPermissionGate scopes={UserPermission.POST_INTERNAL_APPROVE}>
              <animated.div
                style={style}
                className="tw-absolute tw-top-2 tw-z-10 tw-self-center"
                data-name="comments_internal-floating-active-approval-request"
              >
                <Button
                  size="small"
                  className="tw-rounded-full"
                  onClick={scrollToActiveRequestComment}
                >
                  {t('goToApprovalRequest')}
                </Button>
              </animated.div>
            </UserPermissionGate>
          ),
      )}
      <div
        className={clsx('tw-absolute tw-bottom-4 tw-self-center', {
          'tw-bottom-8': showClientAssignedUsers,
        })}
      >
        <GoToNewestCommentButton />
      </div>

      {showClientAssignedUsers && (
        <div className="tw-flex tw-pl-10">
          <CommentsClientAssignedUsers />
        </div>
      )}
    </div>
  );
};

export default CommentsThreads;
